import React, { useState, useEffect } from 'react';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
}

interface Payment {
  id: string;
  amount: string;
  status: string;
  paymentDate: string;
  paymentMethod: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  title: string;
  description: string;
  amount: string;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  type: 'membership' | 'lesson' | 'event' | 'equipment' | 'other';
  isRecurring: boolean;
  recurringFrequency?: 'monthly' | 'quarterly' | 'annually';
  issueDate: string;
  dueDate: string;
  paidAt?: string;
  paidAmount: string;
  createdAt: string;
  family: Family | null;
  creator: {
    firstName: string;
    lastName: string;
  };
  payments: Payment[];
  isNonMember?: boolean;
  nonMemberName?: string;
  nonMemberEmail?: string;
  nonMemberPhone?: string;
  nonMemberCompany?: string;
  paymentToken?: string;
}

interface EditInvoiceModalProps {
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
  families: Family[];
}

export default function EditInvoiceModal({ invoice, onClose, onSuccess, families }: EditInvoiceModalProps) {
  const [formData, setFormData] = useState({
    title: invoice.title,
    description: invoice.description || '',
    amount: invoice.amount,
    type: invoice.type,
    status: invoice.status,
    dueDate: invoice.dueDate.split('T')[0], // Convert to YYYY-MM-DD format
    isRecurring: invoice.isRecurring,
    recurringFrequency: invoice.recurringFrequency || 'monthly',
    membershipLength: (invoice as any).membershipLength || 12,
    notes: '',
    updateRelated: false, // Option to update related membership invoices
    paymentMethod: 'cash',
    paymentNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          ...(formData.isRecurring && formData.recurringFrequency && {
            recurringFrequency: formData.recurringFrequency
          }),
          ...(formData.membershipLength && { membershipLength: formData.membershipLength }),
          updateRelated: formData.updateRelated,
          paymentMethod: formData.paymentMethod,
          paymentNotes: formData.paymentNotes
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      setErrors(['Failed to update invoice. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const canEdit = invoice.status !== 'paid';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">📋 Edit Invoice</h3>
                <p className="text-gray-600 mt-1">{invoice.invoiceNumber}</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Invoice Details */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-4">Invoice Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        disabled={!canEdit}
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          required
                          disabled={!canEdit}
                          min="0.01"
                          step="0.01"
                          value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <select
                        disabled={!canEdit}
                        value={formData.type}
                                                 onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          type: e.target.value as 'membership' | 'lesson' | 'event' | 'equipment' | 'other',
                          // Clear recurring options if switching away from membership
                          isRecurring: e.target.value === 'membership' ? prev.isRecurring : false
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                      >
                        <option value="membership">Membership</option>
                        <option value="lesson">Lesson</option>
                        <option value="event">Event</option>
                        <option value="equipment">Equipment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status 
                        <span className="text-sm font-normal text-gray-500 ml-2">(Manual Override)</span>
                      </label>
                      <select
                        disabled={!canEdit}
                        value={formData.status}
                        onChange={(e) => {
                          const newStatus = e.target.value as 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
                          if (newStatus === 'draft' && invoice.status === 'sent') {
                            if (!window.confirm('⚠️ Are you sure you want to change this invoice back to DRAFT? This will hide it from families.')) {
                              return;
                            }
                          }
                          setFormData(prev => ({ ...prev, status: newStatus }));
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                      >
                        <option value="draft">Draft (Hidden from families)</option>
                        <option value="sent">Sent (Visible to families)</option>
                        <option value="paid">Paid (Payment received)</option>
                        <option value="overdue">Overdue (Past due date)</option>
                        <option value="cancelled">Cancelled (Void invoice)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Override automatic status changes. Use with caution for payment tracking.
                      </p>
                    </div>

                    {/* Payment Method and Notes - shown when status is paid */}
                    {formData.status === 'paid' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Method
                          </label>
                          <select
                            value={formData.paymentMethod}
                            onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                          >
                            <option value="cash">Cash</option>
                            <option value="check">Check</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="manual">Manual Entry</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Notes
                          </label>
                          <textarea
                            rows={2}
                            value={formData.paymentNotes}
                            onChange={(e) => setFormData(prev => ({ ...prev, paymentNotes: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                            placeholder="Optional: Check number, reference, or notes about this payment..."
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Due Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        disabled={!canEdit}
                        value={formData.dueDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                      />
                    </div>

                    {formData.type === 'membership' && (
                      <div className="flex items-center pt-6">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked={formData.isRecurring}
                            onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                            className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Recurring Membership</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {formData.isRecurring && formData.type === 'membership' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Billing Frequency</label>
                        <select
                          disabled={!canEdit}
                          value={formData.recurringFrequency}
                          onChange={(e) => setFormData(prev => ({ ...prev, recurringFrequency: e.target.value as 'monthly' | 'quarterly' | 'annually' }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Membership Length (months)</label>
                        <input
                          type="number"
                          min="1"
                          max="60"
                          disabled={!canEdit}
                          value={formData.membershipLength || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, membershipLength: parseInt(e.target.value) || 12 }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                          placeholder="12"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Total length of the membership in months
                        </p>
                      </div>

                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked={formData.updateRelated}
                            onChange={(e) => setFormData(prev => ({ ...prev, updateRelated: e.target.checked }))}
                            className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded disabled:opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Update all related draft invoices</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          Apply title, description, and amount changes to all future draft invoices in this membership series
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      rows={3}
                      disabled={!canEdit}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green disabled:bg-gray-100"
                      placeholder="Invoice description..."
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Invoice Summary & Details */}
              <div className="space-y-6">
                {/* Family/Contact Information */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {invoice.family ? '👨‍👩‍👧‍👦 Family Information' : '🏢 Contact Information'}
                  </h4>
                  <div className="space-y-2 text-sm">
                    {invoice.family ? (
                      <>
                        <div>
                          <span className="font-medium">Family:</span>
                          <br />
                          <span className="text-gray-700">{invoice.family.familyName}</span>
                        </div>
                        <div>
                          <span className="font-medium">Primary Contact:</span>
                          <br />
                          <span className="text-gray-700">
                            {invoice.family.primaryContactFirstName} {invoice.family.primaryContactLastName}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Email:</span>
                          <br />
                          <span className="text-gray-700">{invoice.family.primaryContactEmail}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="font-medium">Name:</span>
                          <br />
                          <span className="text-gray-700">{invoice.nonMemberName || 'N/A'}</span>
                        </div>
                        {invoice.nonMemberCompany && (
                          <div>
                            <span className="font-medium">Company:</span>
                            <br />
                            <span className="text-gray-700">{invoice.nonMemberCompany}</span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Email:</span>
                          <br />
                          <span className="text-gray-700">{invoice.nonMemberEmail || 'N/A'}</span>
                        </div>
                        {invoice.nonMemberPhone && (
                          <div>
                            <span className="font-medium">Phone:</span>
                            <br />
                            <span className="text-gray-700">{invoice.nonMemberPhone}</span>
                          </div>
                        )}
                        {invoice.paymentToken && (
                          <div>
                            <span className="font-medium">Payment Link:</span>
                            <br />
                            <a
                              href={`${window.location.origin}/pay-invoice/${invoice.paymentToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs break-all"
                            >
                              {`${window.location.origin}/pay-invoice/${invoice.paymentToken}`}
                            </a>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Invoice Summary */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Invoice Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Invoice Number:</span>
                      <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Issue Date:</span>
                      <span className="text-sm">{formatDate(invoice.issueDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Due Date:</span>
                      <span className="text-sm">{formatDate(invoice.dueDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Amount:</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(invoice.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Paid Amount:</span>
                      <span className="text-sm font-medium text-green-600">{formatCurrency(invoice.paidAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Outstanding:</span>
                      <span className="text-sm font-medium text-red-600">
                        {formatCurrency((parseFloat(invoice.amount) - parseFloat(invoice.paidAmount)).toString())}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment History */}
                {invoice.payments && invoice.payments.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Payment History</h4>
                    <div className="space-y-2">
                      {invoice.payments.map(payment => (
                        <div key={payment.id} className="bg-white p-2 rounded border text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium">{formatCurrency(payment.amount)}</span>
                            <span className={`px-1 rounded ${
                              payment.status === 'succeeded' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                          <div className="text-gray-500">
                            {formatDate(payment.paymentDate)} • {payment.paymentMethod}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Creation Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Invoice Details</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Created By:</span>
                      <br />
                      <span className="text-gray-700">{invoice.creator.firstName} {invoice.creator.lastName}</span>
                    </div>
                    <div>
                      <span className="font-medium">Created:</span>
                      <br />
                      <span className="text-gray-700">{formatDate(invoice.createdAt)}</span>
                    </div>
                    {invoice.paidAt && (
                      <div>
                        <span className="font-medium">Paid On:</span>
                        <br />
                        <span className="text-gray-700">{formatDate(invoice.paidAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                {canEdit ? 'Cancel' : 'Close'}
              </button>
              {canEdit && (
                <button
                  type="submit"
                  disabled={loading || !formData.title || !formData.amount || !formData.dueDate}
                  className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : '💾 Update Invoice'}
                </button>
              )}
            </div>

            {!canEdit && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  This invoice cannot be edited because it has been paid. You can view all details above.
                </p>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
} 